// lib/services/push.ts
// Visitor-facing Web Push: subscribe/unsubscribe the public site to new-content
// notifications via the backend's public push endpoints. All client-side.
import { apiMethods } from './api';

const SW_URL = '/sw.js';

interface BrowserPushSubscription {
    endpoint: string;
    keys: { p256dh: string; auth: string };
}

export function isPushSupported(): boolean {
    return (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    );
}

export function getPermission(): NotificationPermission | 'unsupported' {
    if (!isPushSupported()) return 'unsupported';
    return Notification.permission;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const buffer = new ArrayBuffer(rawData.length);
    const output = new Uint8Array(buffer);
    for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i);
    return output;
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
    const existing = await navigator.serviceWorker.getRegistration(SW_URL);
    if (existing) return existing;
    return navigator.serviceWorker.register(SW_URL);
}

export async function getCurrentSubscription(): Promise<BrowserPushSubscription | null> {
    if (!isPushSupported()) return null;
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!reg) return null;
    const sub = await reg.pushManager.getSubscription();
    return sub ? (sub.toJSON() as BrowserPushSubscription) : null;
}

// Subscribe THIS device to new-content notifications. Throws on denial/failure.
export async function subscribeVisitor(): Promise<string> {
    if (!isPushSupported()) throw new Error('Push notifications are not supported in this browser');

    const res = await apiMethods.get<{ data: { publicKey: string } }>('/push/vapid-public-key');
    const publicKey = res?.data?.publicKey;
    if (!publicKey) throw new Error('Push notifications are not configured');

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
        throw new Error(
            permission === 'denied'
                ? 'Notifications are blocked. Enable them in your browser settings.'
                : 'Notification permission was not granted.',
        );
    }

    const reg = await getRegistration();
    await navigator.serviceWorker.ready;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
    }
    const json = sub.toJSON() as BrowserPushSubscription;
    await apiMethods.post('/push/public/subscribe', json);
    return json.endpoint;
}

// Unsubscribe THIS device (browser + backend).
export async function unsubscribeVisitor(): Promise<void> {
    const current = await getCurrentSubscription();
    if (!isPushSupported()) return;
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) await sub.unsubscribe();
    if (current?.endpoint) {
        await apiMethods.delete('/push/public/unsubscribe', { data: { endpoint: current.endpoint } }).catch(() => {});
    }
}
